import { useContext } from 'react';
import {
    GelRowLayout,
    GelBoxLayout,
    GelScreenDetectorContext,
    GelParagraph
} from '@tal-gel/components';
import { getGelTokens } from '@tal-gel/theming';
import { HorizontalSeperator, VerticalSeperator } from './dividers';
import { ClaimDetails } from '../../../common/models/memberclaims';
import { useGetClaimDetailsContent } from '../../../common/api/graphQLDataFetch';
import { formatCurrency, Spacer, StyledGelLabel } from './common';

interface ClaimConsultantDetailsProps {
    caseManager: string;
}
const ClaimConsultantDetails = ({caseManager}:ClaimConsultantDetailsProps) => {
    const { global: { sizeBaseX5 } } = getGelTokens()
    const { data } = useGetClaimDetailsContent()

    return (
        <GelRowLayout style={{ paddingBottom: sizeBaseX5, minWidth: sizeBaseX5 * 5 }} gutter="small">
            <GelBoxLayout>
                <StyledGelLabel small id={"caseManagerLabel"}>{data?.policyInformation?.CaseManager}</StyledGelLabel>
                <GelParagraph small id={"caseManagerValue"} style={{ display: "flex", justifyContent: "end", fontWeight: "400" }}>
                    {caseManager ?? data?.policyInformation?.DefaultCaseManager}
                </GelParagraph>
            </GelBoxLayout>
        </GelRowLayout>
    )
}

interface TpdBenefitDetailsProps {
    details: ClaimDetails;
}
const TpdBenefitDetails = ({details: {sumInsured}}:TpdBenefitDetailsProps) => {
    const { global: { sizeBaseX5 } } = getGelTokens()
    const { data } = useGetClaimDetailsContent()

    return (
        <GelRowLayout style={{ paddingBottom: sizeBaseX5, minWidth: sizeBaseX5 * 5 }} gutter="small">
            <GelBoxLayout alignment="start">
                <StyledGelLabel small id={"benefitAmountLabel"}>{data?.policyInformation?.BenefitAmount}</StyledGelLabel>
                <GelParagraph small id={"benefitAmountValue"} style={{ display: "flex", justifyContent: "end", fontWeight: "400" }}>
                    {formatCurrency(sumInsured)}
                </GelParagraph>
            </GelBoxLayout>
        </GelRowLayout>
    )
}

interface TpdPolicyDetailsProps {
    details: ClaimDetails;
}
const TpdPolicyDetails = ({details: {policyOwner, masterPolicyNo, lifeInsured}}: TpdPolicyDetailsProps) => {
    const { global: { sizeBaseX5 } } = getGelTokens()
    const { data } = useGetClaimDetailsContent()
    
    return (
        <GelRowLayout style={{ paddingBottom: sizeBaseX5 }} gutter="small">
            <GelBoxLayout alignment="start">
                <StyledGelLabel small id={"policyOwnerLabel"}>{data?.policyInformation?.PolicyOwner}</StyledGelLabel>
                <GelParagraph small id={"policyOwnerValue"} style={{ display: "flex", textAlign: "end", justifyContent: "end", fontWeight: "400" }}>
                    {policyOwner}
                </GelParagraph>
            </GelBoxLayout>
            <GelBoxLayout alignment="start">
                <StyledGelLabel small id={"policyNumberLabel"}>{data?.policyInformation?.PolicyNumber}</StyledGelLabel>
                <GelParagraph small id={"policyNumberValue"} style={{ display: "flex", justifyContent: "end", fontWeight: "400" }}>
                    {masterPolicyNo}
                </GelParagraph>
            </GelBoxLayout>
            <GelBoxLayout alignment="start">
                <StyledGelLabel small id={"lifeInsuredLabel"}>{data?.policyInformation?.LifeInsured}</StyledGelLabel>
                <GelParagraph small id={"lifeInsuredValue"} style={{ display: "flex", justifyContent: "end", fontWeight: "400", textAlign: "right" }}>
                    {lifeInsured}
                </GelParagraph>
            </GelBoxLayout>
        </GelRowLayout>
    )
}

interface TPDDetailsProps {
    details: ClaimDetails;
    caseManager: string;
}
const TPDDetails = ({details, caseManager}: TPDDetailsProps) => {
    const { screen, isXsScreen, isLargerThanLgScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;    
    const { global: { sizeBaseUnit } } = getGelTokens()

    if (isXsScreen(screen)) {
        return (
            <GelRowLayout>
                <TpdPolicyDetails details={details}/>
                <HorizontalSeperator />
                <Spacer></Spacer>
                <TpdBenefitDetails details={details}/>
                <HorizontalSeperator />
                <Spacer></Spacer>
                <ClaimConsultantDetails caseManager={caseManager}/>
            </GelRowLayout>
        );
    }
    else {
        return (
            <GelBoxLayout space={[5, 0.5, 5, 0.5, 5]} alignment="start">
                <TpdPolicyDetails details={details} />
                <VerticalSeperator style={{ height: !isLargerThanLgScreen(screen) ? sizeBaseUnit * 45 : sizeBaseUnit * 30 }} />
                <TpdBenefitDetails details={details} />
                <VerticalSeperator style={{ height: !isLargerThanLgScreen(screen) ? sizeBaseUnit * 45 : sizeBaseUnit * 30 }} />
                <ClaimConsultantDetails caseManager={caseManager} />
            </GelBoxLayout>
        );
    }
};

export default TPDDetails;